import type { I18n } from '@lingui/core';
import { t } from '@lingui/core/macro';
/** Providers selectable on the "General" create-agent tab, used purely to
 * pick which connection instructions to show after creation — the server
 * doesn't store this choice, it only decides how the agent's local runtime
 * host process gets configured. The order is the order every provider list
 * in the UI presents them in.
 */
export const AGENT_PROVIDERS = [
  'claude',
  'copilot',
  'codex',
  'dsh',
  'openclaw',
  'hermes',
  'praestoclaw',
  'kosmos',
] as const;
export type AgentProvider = (typeof AGENT_PROVIDERS)[number];

export const AGENT_PROVIDER_LABELS: Record<AgentProvider, string> = {
  claude: 'Claude Code CLI',
  copilot: 'GitHub Copilot CLI',
  codex: 'Codex CLI',
  dsh: 'DeepSeek Harness',
  openclaw: 'OpenClaw',
  hermes: 'Hermes Agent',
  praestoclaw: 'PraestoClaw',
  kosmos: 'Kosmos Desktop',
};

export type Platform = 'windows' | 'linux';

/** Returns true if the provider has different commands per platform. */
export function providerHasPlatformDifference(provider: AgentProvider): boolean {
  return provider === 'praestoclaw' || provider === 'hermes' || provider === 'dsh';
}

/**
 * Builds the copy-paste command block for connecting a local
 * runtime to a freshly-created agent.
 *
 * Returns an array of command blocks, each rendered as a separate
 * copyable code block in the UI.
 */
export function buildAgentConnectCommand(
  i18n: I18n,
  provider: AgentProvider,
  serverUrl: string,
  apiKey: string,
  agentName: string,
  platform: Platform = 'linux',
): string {
  return buildAgentConnectCommandParts(i18n, provider, serverUrl, apiKey, agentName, platform).join(
    '\n\n',
  );
}

export function buildAgentConnectCommandParts(
  i18n: I18n,
  provider: AgentProvider,
  serverUrl: string,
  apiKey: string,
  agentName: string,
  platform: Platform = 'linux',
): string[] {
  if (provider === 'openclaw') {
    const slug = agentName.replace(/\s+/g, '_').toLowerCase();
    return [
      `# ${t(i18n)`Step 1: Install OpenClaw (skip if already installed)`}\nnpm i -g openclaw@latest`,
      `# ${t(i18n)`Step 2: Install the Borgee plugin`}\nopenclaw plugins install @borgee/openclaw-plugin\n\n# ${t(i18n)`Step 3: Configure your account`}\nopenclaw config set channels.borgee.accounts.${slug}.baseUrl '"${serverUrl}"'\nopenclaw config set channels.borgee.accounts.${slug}.apiKey '"${apiKey}"'\nopenclaw config set channels.borgee.allowFrom '[\"*\"]'\nopenclaw config set channels.borgee.groupAllowFrom '[\"*\"]'\n\n# ${t(i18n)`Step 4: Enable token-usage telemetry and restart`}\n# ${t(i18n)`This lets Borgee read the final turn's usage. It does not persist message content.`}\nopenclaw config set plugins.entries.borgee.enabled true\nopenclaw config set plugins.entries.borgee.hooks.allowConversationAccess true\nopenclaw gateway restart`,
    ];
  }

  if (provider === 'kosmos') {
    return [
      `# ${t(i18n)`Step 1: Open Kosmos Desktop`}\n${t(i18n)`Open Settings → Remote Channel → Borgee, then select the local Kosmos agent that should answer Borgee messages.`}`,
      `# ${t(i18n)`Step 2: Copy and paste this Server URL`}\n${serverUrl}`,
      `# ${t(i18n)`Step 3: Copy and paste this API key`}\n${apiKey}`,
      `# ${t(i18n)`Step 4: Click Connect`}\n${t(i18n)`Return to Borgee and wait for ${agentName} to become online.`}`,
    ];
  }

  if (provider === 'claude') {
    return [
      `# ${t(i18n)`Step 1: Install Claude Code (skip if already installed)`}\nnpm i -g @anthropic-ai/claude-code@latest`,
      `# ${t(i18n)`Step 2: Install Borgee agents-host`}\nnpm i -g @borgee/agents-host\n\n# ${t(i18n)`Step 3: Connect this agent`}\nagents-host start ${serverUrl} ${apiKey} --name "${agentName}" --provider claude`,
    ];
  }

  if (provider === 'codex') {
    return [
      `# ${t(i18n)`Step 1: Install Borgee agents-host`}\nnpm i -g @borgee/agents-host`,
      `# ${t(i18n)`Step 2: Connect this agent`}\nagents-host start ${serverUrl} ${apiKey} --name "${agentName}" --provider codex`,
    ];
  }

  if (provider === 'dsh') {
    return buildDshConnectCommandParts(i18n, serverUrl, apiKey, platform);
  }

  if (provider === 'hermes') {
    const hermesInstall =
      platform === 'windows'
        ? `# ${t(i18n)`Step 1: Install Hermes Agent (PowerShell)`}\niex (irm https://hermes-agent.nousresearch.com/install.ps1)`
        : `# ${t(i18n)`Step 1: Install Hermes Agent`}\ncurl -fsSL https://hermes-agent.nousresearch.com/install.sh | bash`;
    return [
      hermesInstall,
      `# ${t(i18n)`Step 2: Install Borgee plugin (requires Python 3.11+)`}\npip install borgee-hermes-plugin`,
      `# ${t(i18n)`Step 3: Setup Borgee platform config`}\nborgee-hermes setup --url ${serverUrl} --api-key ${apiKey}`,
      `# ${t(i18n)`Step 4: Start Hermes`}\nhermes gateway run`,
    ];
  }

  if (provider === 'praestoclaw') {
    const installStep =
      platform === 'windows'
        ? `# ${t(i18n)`Step 1: Install PraestoClaw`}\nirm https://aka.ms/praestoclaw/install.ps1 | iex`
        : `# ${t(i18n)`Step 1: Install PraestoClaw`}\ncurl -fsSL https://aka.ms/praestoclaw/install.sh | bash`;

    return [
      installStep,
      `# ${t(i18n)`Step 2: Install the Borgee bridge plugin`}\nnpm i -g @borgee/praestoclaw-plugin`,
      `# ${t(i18n)`Step 3: Configure PraestoClaw API key for the bridge`}\nborgee-praestoclaw setup`,
      `# ${t(i18n)`Step 4: Restart PraestoClaw to load the new key`}\npc s`,
      `# ${t(i18n)`Step 5: Connect this agent (open a new terminal)`}\nborgee-praestoclaw --borgee-url ${serverUrl} --borgee-api-key ${apiKey}`,
    ];
  }

  // copilot
  return [
    `# ${t(i18n)`Step 1: Install GitHub Copilot (skip if already installed)`}\nnpm i -g @github/copilot`,
    `# ${t(i18n)`Step 2: Install Borgee agents-host`}\nnpm i -g @borgee/agents-host\n\n# ${t(i18n)`Step 3: Connect this agent`}\nagents-host start ${serverUrl} ${apiKey} --name "${agentName}" --provider copilot`,
  ];
}

/**
 * The connector is a plain registry dependency of the DSH Web profile, not a
 * DSH profile bundle: it declares no `dsh.bundle`, so the owner wires it in
 * through their own Cordis patch layer and `dsh plugin add` says as much on
 * stderr. The install block names that warning, or a correct install reads as a
 * failed one.
 *
 * Two things are load-bearing in the profile block. `cwd` is the DSH session's
 * working directory — the directory the agent itself operates in, which is not
 * where the connector is installed — so it is emitted as a placeholder for the
 * owner to replace rather than resolved from the shell. And `cordis.patch.yml`
 * is the owner's own patch layer, which may already hold their overrides, so
 * the block appends to it; the `[]` the profile template starts life with has
 * to be dropped on the way, because a flow sequence cannot be followed by
 * block-sequence entries.
 */
function buildDshConnectCommandParts(
  i18n: I18n,
  serverUrl: string,
  apiKey: string,
  platform: Platform,
): string[] {
  const patchEntry = (cwd: string) =>
    [
      `- insert:`,
      `    - id: borgee-dsh`,
      `      name: '@borgee/dsh-plugin'`,
      `      config:`,
      `        baseUrl: '${serverUrl}'`,
      `        apiKey: '${apiKey}'`,
      `        # ${t(i18n)`The directory this agent works in, not where the connector is installed.`}`,
      `        cwd: '${cwd}'`,
      `        presetId: standard`,
      `        sessionPrefix: borgee-dsh-v1`,
    ].join('\n');
  const patchHeader = [
    `# ${t(i18n)`Add the connector to your DSH Web patch layer, keeping any patches already in it`}`,
    `# ${t(i18n)`Replace the cwd placeholder below before you run this`}`,
    `# ${t(i18n)`Run this once — if the file already lists borgee-dsh, edit that entry instead`}`,
  ].join('\n');
  const restart = `# ${t(i18n)`Restart DSH Web`}\n# ${t(i18n)`Stop the running dsh web process with Ctrl+C, then run:`}\ndsh web`;

  const install = [
    `# ${t(i18n)`Install the connector into your DSH Web profile`}`,
    `# ${t(i18n)`dsh warns`} "@borgee/dsh-plugin declares no dsh.bundle — installed as a plain dependency, not a profile layer"`,
    `# ${t(i18n)`That warning is expected: the connector is configured through the patch layer below`}`,
    `dsh plugin --profile web add @borgee/dsh-plugin`,
  ].join('\n');

  const profile =
    platform === 'windows'
      ? `${patchHeader}\n$patch = "$env:USERPROFILE\\.dsh\\profiles\\web\\cordis.patch.yml"\n$entry = @'\n${patchEntry('C:\\path\\to\\your\\project')}\n'@\nNew-Item (Split-Path $patch) -ItemType Directory -Force | Out-Null\n$kept = @(if (Test-Path $patch) { Get-Content $patch }) -notmatch '^\\[\\]$'\nSet-Content $patch ($kept + $entry)`
      : `${patchHeader}\nmkdir -p ~/.dsh/profiles/web\ntouch ~/.dsh/profiles/web/cordis.patch.yml\ngrep -v '^\\[\\]$' ~/.dsh/profiles/web/cordis.patch.yml > ~/.dsh/profiles/web/cordis.patch.yml.new\ncat >> ~/.dsh/profiles/web/cordis.patch.yml.new <<'YAML'\n${patchEntry('/path/to/your/project')}\nYAML\nmv ~/.dsh/profiles/web/cordis.patch.yml.new ~/.dsh/profiles/web/cordis.patch.yml`;

  return [install, profile, restart];
}

const PROVIDER_STORAGE_PREFIX = 'borgee:agent-connect-provider:';

/**
 * Client-only "last selected provider" memory, keyed by agent id. This is not
 * live connection status. It remembers what the owner picked last time so the
 * detail page does not reset to "OpenClaw" whenever they reopen it.
 */
export function getStoredAgentProvider(agentId: string): AgentProvider | null {
  try {
    const raw = window.localStorage.getItem(PROVIDER_STORAGE_PREFIX + agentId);
    return raw !== null && (AGENT_PROVIDERS as readonly string[]).includes(raw)
      ? (raw as AgentProvider)
      : null;
  } catch {
    return null;
  }
}

export function setStoredAgentProvider(agentId: string, provider: AgentProvider): void {
  try {
    window.localStorage.setItem(PROVIDER_STORAGE_PREFIX + agentId, provider);
  } catch {
    // localStorage may be unavailable (privacy mode, quota); losing this
    // preference is harmless, so fail silently.
  }
}

/**
 * Derives the agent provider from the server-side last_connected_plugin_id.
 * "" = openclaw (sends no plugin_id), "agents-host:<provider>" = CLI agents.
 * Falls back to localStorage, then to 'claude'.
 */
export function resolveAgentProvider(
  agentId: string,
  lastConnectedPluginId?: string,
): AgentProvider {
  if (lastConnectedPluginId !== undefined) {
    if (lastConnectedPluginId === '') return 'openclaw';
    const match = lastConnectedPluginId.match(/^agents-host:(.+)$/);
    if (match) {
      const p = match[1];
      if ((AGENT_PROVIDERS as readonly string[]).includes(p)) return p as AgentProvider;
    }
    if (lastConnectedPluginId === 'dsh-cordis') return 'dsh';
    if (lastConnectedPluginId.includes('praestoclaw')) return 'praestoclaw';
    if (lastConnectedPluginId.includes('hermes')) return 'hermes';
  }
  return getStoredAgentProvider(agentId) ?? 'claude';
}

export function buildAgentDisconnectCommandParts(
  i18n: I18n,
  provider: AgentProvider,
  agentName: string,
  platform: Platform = 'linux',
): string[] {
  if (provider === 'openclaw') {
    return [
      `# ${t(i18n)`Step 1: Disable the Borgee plugin`}\nopenclaw config set plugins.entries.borgee.enabled false`,
      `# ${t(i18n)`Step 2: Restart the gateway`}\nopenclaw gateway restart`,
    ];
  }

  if (provider === 'dsh') {
    return [
      `# ${t(i18n)`Stop DSH Web`}
# ${t(i18n)`Press Ctrl+C in the terminal running dsh web`}`,
    ];
  }

  if (provider === 'hermes') {
    return [
      `# ${t(i18n)`Stop the Hermes server`}\n# ${t(i18n)`Press Ctrl+C in the terminal running hermes server`}`,
    ];
  }

  if (provider === 'praestoclaw') {
    return [
      platform === 'windows'
        ? `# ${t(i18n)`Stop the borgee-praestoclaw bridge`}\n# ${t(i18n)`Press Ctrl+C in the terminal running borgee-praestoclaw`}`
        : `# ${t(i18n)`Stop the borgee-praestoclaw bridge`}\n# ${t(i18n)`Press Ctrl+C in the terminal running borgee-praestoclaw`}`,
    ];
  }

  if (provider === 'kosmos') {
    return [
      `# ${t(i18n)`In Kosmos Desktop`}
${t(i18n)`Open Settings → Remote Channel → Borgee, then click Disconnect.`}`,
    ];
  }

  // claude / copilot / codex — all use agents-host as a foreground process
  return [
    `# ${t(i18n)`Stop agents-host`}\n# ${t(i18n)`Press Ctrl+C in the terminal running agents-host`}`,
  ];
}
